<template>
  <div class="flex h-full w-full justify-center items-center">
    <SkeletonLoader v-if="loading" :page="loaderPages.TABLE" />
    <div v-else-if="emails?.length > 0" class="h-full w-full">
      <CustomTable
        height="(68vh - var(--banner-height))"
        :headers="emailHeaders"
        :data="emails"
        :custom-column-render="customColumnRender"
        :show-actions-column="true"
        :actions="actions"
        :pagination="pagination"
        :show-checkboxes="true"
        :selection="selection"
        identifier-key="id"
        @update:pagination="handlePageChange"
        @update:selection="handleSelectionUpdate"
        :toShowActions="() => {}"
        prefixImageBorderRadius="0%"
        :source="source"
        :email-filter-data="emailFilterData"
        @entity-list-created="toAddEntityLists"
        :show-select-all="source !== entityTypes.SEQUENCE_EMAILS"
      />
    </div>
    <EmptyScreen v-else source="emails_table" text="No emails found!" />
  </div>
</template>

<script setup>
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import SentTo from "@/src/components/emails/renderers/SentTo.vue";
import EmailContent from "@/src/components/emails/renderers/EmailContent.vue";
import Engagement from "@/src/components/emails/renderers/Engagement.vue";
import Sender from "@/src/components/emails/renderers/Sender.vue";
import SentOn from "@/src/components/emails/renderers/SentOn.vue";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import EmailService from "@/src/api/EmailService.js";
import { message } from "ant-design-vue";

import { imageNames } from "@/src/constants/imageConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";

const props = defineProps({
  emails: { type: Array, required: true },
  pagination: { type: Object, required: true },
  loading: { type: Boolean, required: true },
  handlePageChange: { type: Function, required: true },
  composeEmailReply: { type: Function, required: true },
  selection: { type: Object, required: true },
  source: { type: String, required: true },
  emailFilterData: { type: Object, default: () => ({}) },
  source: { type: String, required: true },
});

const emit = defineEmits(["update:selection"]);

const emailHeaders = [
  { label: "Sent To", key: "sent_to", width: 2 },
  { label: "Email", key: "email", width: 5 },
  { label: "Engagement", key: "engagement", width: 2 },
  { label: "Sender", key: "sender", width: 1 },
  { label: "Sent On", key: "sent_on", width: 2 },
];

const actions = [
  {
    label: "Reply",
    handler: (row) => props.composeEmailReply(row),
    icon: imageNames.REPLY_ALL,
  },
];

const handleSelectionUpdate = (newSelection) => {
  emit("update:selection", newSelection);
};

const clearSelections = () => {
  emit("update:selection", {
    selected_ids: [],
    unselected_ids: [],
    unselected_emails: [],
    select_all: false,
  });
};

const extractEmailsFromSelection = () => {
  return props.selection.selected_ids
    .map((id) => {
      const email = props.emails.find((row) => row.id === id);
      return email?.sent_to?.email;
    })
    .filter(Boolean);
};

const toAddEntityLists = async ({ listId, rowId = null }) => {
  try {
    let payload;

    if (rowId) {
      const singleEmail = props.emails.find((email) => email.id === rowId);
      const selectedEmails = [singleEmail?.sent_to?.email].filter(Boolean);

      if (selectedEmails.length === 0) {
        message.error("No valid email found to add to list");
        return;
      }

      payload = {
        list_id: listId,
        selected_emails: selectedEmails,
      };
    } else {
      if (props.selection.select_all) {
        payload = {
          list_id: listId,
          select_all: true,
          unselected_emails: props.selection.unselected_emails || [],
        };
      } else {
        const selectedEmails = extractEmailsFromSelection();
        if (selectedEmails.length === 0) {
          message.error("No valid emails found to add to list");
          return;
        }

        payload = {
          list_id: listId,
          selected_emails: selectedEmails,
        };
      }
    }

    const response = await EmailService.addEmailsToList(
      payload,
      props.emailFilterData,
    );

    if (response?.success) {
      message.success("Leads/Contacts added to the list successfully");
      clearSelections();
    } else {
      message.error("Failed to add emails to the list");
    }
  } catch (error) {
    message.error("An error occurred while adding emails to the list");
    console.error("Error while adding emails to list: ", error);
  }
};

const customColumnRender = {
  sent_to: SentTo,
  email: EmailContent,
  engagement: {
    component: Engagement,
    props: (row) => ({
      timestampData: {
        deliveredTimestamp: row.sent_on,
        openedTimestamps: row.engagement?.opened_created_at || [],
        clickedTimestamps: row.engagement?.clicked_created_at || [],
        repliedTimestamps: row.engagement?.replied_created_at || [],
        originalOpenedCount: row.engagement?.opened_count || 0,
        originalClickedCount: row.engagement?.clicked_count || 0,
        originalRepliedCount: row.engagement?.replied_count || 0,
      },
      showHoverDetails: true,
    }),
  },
  sender: Sender,
  sent_on: SentOn,
};
</script>
